import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { BsClock } from "react-icons/bs";

interface InactivityModalProps {
  show: boolean;
  // Unix epoch seconds — the actual moment we'll log the user out if they
  // don't act. AuthContext owns this; the modal just renders a countdown
  // toward it and fires the appropriate callback.
  expiresAt: number | null;
  onStayLoggedIn: () => void;
  onLogout: () => void;
  isRefreshing?: boolean;
}

// Surfaces 3 minutes before the access token expires. If the user clicks
// "Stay logged in," AuthContext calls /api/refreshToken which trades the
// stored refresh token for a fresh access token, and reschedules the next
// warning. If the countdown runs out without action, AuthContext calls
// logout. Modal is non-dismissible (no X, no backdrop click) so users
// can't accidentally close it and miss the warning.
const InactivityModal: React.FC<InactivityModalProps> = ({
  show,
  expiresAt,
  onStayLoggedIn,
  onLogout,
  isRefreshing = false,
}) => {
  const [secondsLeft, setSecondsLeft] = useState<number>(0);

  useEffect(() => {
    if (!show || !expiresAt) return;
    const tick = () => {
      const remaining = Math.max(0, Math.floor(expiresAt - Date.now() / 1000));
      setSecondsLeft(remaining);
      if (remaining === 0) onLogout();
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [show, expiresAt, onLogout]);

  const mm = Math.floor(secondsLeft / 60);
  const ss = secondsLeft % 60;
  const formatted = `${mm}:${ss.toString().padStart(2, "0")}`;

  return (
    <Modal
      show={show}
      onHide={() => {}}
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Header style={{ borderBottom: "1px solid #f3f4f6" }}>
        <Modal.Title style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "18px" }}>
          <BsClock size={20} color="#f59e0b" />
          Are you still there?
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: "24px" }}>
        <div style={{ fontSize: "14px", color: "#374151", marginBottom: "16px" }}>
          Your session will expire soon due to inactivity. Click "Stay logged in"
          to continue, or you'll be signed out automatically.
        </div>
        <div
          style={{
            background: secondsLeft < 30 ? "#fef2f2" : "#fffbeb",
            border: `1px solid ${secondsLeft < 30 ? "#fecaca" : "#fde68a"}`,
            borderRadius: "10px",
            padding: "20px",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>
            Signing out in
          </div>
          <div
            style={{
              fontSize: "44px",
              fontWeight: 700,
              fontVariantNumeric: "tabular-nums",
              color: secondsLeft < 30 ? "#dc2626" : "#d97706",
              lineHeight: 1,
            }}
          >
            {formatted}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer style={{ borderTop: "1px solid #f3f4f6", justifyContent: "space-between" }}>
        <Button variant="outline-secondary" onClick={onLogout} disabled={isRefreshing}>
          Sign out now
        </Button>
        <Button
          variant="primary"
          onClick={onStayLoggedIn}
          disabled={isRefreshing}
          style={{ minWidth: "160px" }}
        >
          {isRefreshing ? "Extending..." : "Stay logged in"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default InactivityModal;
